import { Component, inject, LOCALE_ID, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, forkJoin, Observable, of } from 'rxjs';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  NativeDateAdapter,
} from '@angular/material/core';
import { MY_DATE_FORMATS } from '../../news/edit-detail-incorporate/edit-detail-incorporate.component';

registerLocaleData(localeEs);

// Custom date adapter that starts weeks on Monday
class SpanishDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1; // Monday
  }
}
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { HolidaysService } from '../../../shared/services/holidays/holidays.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  OperativeTableRow,
  Operative,
  OperativaGroup,
  ExerciseOperativeAssignment,
} from '../../../shared/interfaces/holidays.interface';

@Component({
  selector: 'app-exercise-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
    MatTableModule,
    MatSortModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: DateAdapter, useClass: SpanishDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  templateUrl: './exercise-form.component.html',
  styleUrls: [
    './exercise-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ExerciseFormComponent {
  dataSource: MatTableDataSource<OperativeTableRow> =
    new MatTableDataSource<OperativeTableRow>();
  displayedColumns: string[] = ['operativa_group', 'assigned'];
  private _liveAnnouncer = inject(LiveAnnouncer);

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  year?: number;

  // Mode management
  isEditMode: boolean = false;
  exerciseId: number | null = null;
  backLink: string = '/home/vacations/drivers';

  // Common properties
  myroles: any = {};
  exerciseForm: FormGroup;
  exercise: any = {};
  isSubmitting = false;
  attemptedSubmit = false;
  dateRangeError = false;
  vacationDateRangeError = false;
  vacationDateIncompleteError = false;
  requestDateRangeError = false;
  requestDateIncompleteError = false;

  // Operative table data
  operativeTableData: OperativeTableRow[] = [];
  originalAssignments: Set<number> = new Set(); // Track original assignments

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.exerciseForm = this.fb.group({
      name: ['', [Validators.required]],
      year: [
        '',
        [Validators.required, Validators.min(2000), Validators.max(2100)],
      ],
      start_date: ['', [Validators.required]],
      end_date: ['', [Validators.required]],
      active: [true],
      // Configuration fields
      start_request_holidays: [''],
      end_request_holidays: [''],
      start_vacation_period: [''],
      end_vacation_period: [''],
      continuous_phase: [false],
      initial_petition: [true],
    });

    // Add date range validation
    this.exerciseForm.valueChanges.subscribe(() => {
      this.validateDateRange();
    });

    // Add mutual exclusivity logic for continuous_phase and initial_petition
    this.exerciseForm
      .get('continuous_phase')
      ?.valueChanges.subscribe((value) => {
        if (value === true) {
          this.exerciseForm
            .get('initial_petition')
            ?.setValue(false, { emitEvent: false });
        }
      });

    this.exerciseForm
      .get('initial_petition')
      ?.valueChanges.subscribe((value) => {
        if (value === true) {
          this.exerciseForm
            .get('continuous_phase')
            ?.setValue(false, { emitEvent: false });
        }
      });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.exerciseId = +params['id'];
        this.year = +params['year'];
        this.backLink = `/home/vacations/drivers/exercise/${this.exerciseId}/${this.year}`;
        this.loaderService.showLoader();
        this.loadExerciseData();
      } else {
        this.isEditMode = false;
        this.exerciseId = null;
        this.backLink = '/home/vacations/drivers';
        // Set default year to current year
        this.exerciseForm.patchValue({
          year: new Date().getFullYear(),
        });
        // Load operatives for create mode
        this.loadOperatives();
      }
    });
    // this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar un ejercicio'
        : 'No tienes permisos para añadir un ejercicio';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/vacations/drivers',
        permissionMessage
      );
    }
  }

  private loadExerciseData(): void {
    if (!this.exerciseId) return;

    // Load exercise data and operativa groups concurrently
    forkJoin({
      exercise: this.holidaysService.getExerciseById(this.exerciseId, false),
      groups: this.holidaysService.getGroups({ active: true, mock: false }),
      exerciseOperatives: this.holidaysService.getExerciseOperatives(
        this.exerciseId
      ),
    }).subscribe({
      next: ({ exercise, groups, exerciseOperatives }) => {
        this.exercise = exercise;

        // Set the form values from the loaded exercise data
        this.exerciseForm.patchValue({
          name: this.exercise.name,
          year: this.exercise.year,
          start_date: this.exercise.start_date,
          end_date: this.exercise.end_date,
          active: this.exercise.active,
          // Configuration fields
          start_request_holidays:
            this.exercise['configuration']?.start_request_holidays || '',
          end_request_holidays:
            this.exercise['configuration']?.end_request_holidays || '',
          start_vacation_period:
            this.exercise['configuration']?.start_vacation_period || '',
          end_vacation_period:
            this.exercise['configuration']?.end_vacation_period || '',
          continuous_phase:
            this.exercise['configuration']?.continuous_phase || false,
          initial_petition:
            this.exercise['configuration']?.initial_petition || false,
        });

        // Combine operativa groups and exercise assignments for edit mode
        this.combineOperativesData(groups, exerciseOperatives);
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching exercise data', error);
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private loadOperatives(): void {
    this.loaderService.showLoader();
    this.holidaysService.getGroups({ active: true, mock: false }).subscribe({
      next: (groups: OperativaGroup[]) => {
        // Map groups to table rows (already filtered by active on server)
        this.operativeTableData = groups.map((group) => ({
          id: group.id,
          name: group.name,
          active: group.active,
          isAssigned: false, // In create mode, nothing is assigned yet
        }));

        this.dataSource.data = this.operativeTableData;
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching operativa groups', error);
        this.snackBar.open(
          'Error al cargar los grupos de operativas',
          'Cerrar',
          {
            duration: 3000,
          }
        );
      },
    });
  }

  private combineOperativesData(
    groups: OperativaGroup[],
    exerciseOperatives: ExerciseOperativeAssignment
  ): void {
    // console.log('Combining operatives data:', { groups, exerciseOperatives });

    // Groups are already filtered by active on server
    // Create a Set of assigned group IDs with active assignments
    const assignedGroupIds = new Set<number>();
    const assignmentMap = new Map<number, number>(); // group_id -> assignment_id

    // The API structure has operativa_groups with operatives[] that contain protocol_assignment
    if (
      exerciseOperatives &&
      Array.isArray(exerciseOperatives.operativa_groups)
    ) {
      exerciseOperatives.operativa_groups.forEach((group: any) => {
        // Check if any operative within the group has an active protocol_assignment
        const activeOperative = group.operatives?.find(
          (op: any) => op.protocol_assignment?.status === 'active'
        );
        if (activeOperative) {
          assignedGroupIds.add(group.id);
          assignmentMap.set(group.id, activeOperative.protocol_assignment.id);
        }
      });
    }

    // console.log('Assigned group IDs:', Array.from(assignedGroupIds));
    // console.log('Assignment map:', Array.from(assignmentMap.entries()));

    // Map operativa groups to table rows
    this.operativeTableData = groups.map((group) => ({
      id: group.id,
      name: group.name,
      active: group.active,
      isAssigned: assignedGroupIds.has(group.id),
      assignmentId: assignmentMap.get(group.id),
    }));

    // Store original assignments for comparison when saving
    this.originalAssignments = new Set(
      this.operativeTableData
        .filter((row) => row.isAssigned)
        .map((row) => row.id)
    );

    // console.log('Operative table data:', this.operativeTableData);
    // console.log('Original assignments:', Array.from(this.originalAssignments));

    this.dataSource.data = this.operativeTableData;
  }

  getSelectedOperatives(): number[] {
    return this.operativeTableData
      .filter((row) => row.isAssigned)
      .map((row) => row.id);
  }

  private saveOperativeAssignments(exerciseId: number): Observable<any> {
    const selectedOperatives = this.getSelectedOperatives();

    // If no groups selected, return empty observable
    if (selectedOperatives.length === 0) {
      return of(null);
    }

    // Format body based on number of groups
    let body: any;
    if (selectedOperatives.length === 1) {
      // Single group
      body = {
        operativa_group: selectedOperatives[0],
        exercise: exerciseId,
        status: 'active',
      };
    } else {
      // Multiple groups
      body = {
        operativa_groups: selectedOperatives,
        exercise: exerciseId,
      };
    }

    // console.log('Saving operativa group assignments:', body);
    return this.holidaysService.postExerciseOperatives(body, exerciseId);
  }

  private unassignOperatives(): Observable<any> {
    const currentlySelected = new Set(this.getSelectedOperatives());
    const toUnassign: { id: number; assignmentId: number }[] = [];

    // Find operatives that were originally assigned but are now unchecked
    this.operativeTableData.forEach((row) => {
      if (
        this.originalAssignments.has(row.id) &&
        !currentlySelected.has(row.id) &&
        row.assignmentId
      ) {
        toUnassign.push({ id: row.id, assignmentId: row.assignmentId });
      }
    });

    // If nothing to unassign, return empty observable
    if (toUnassign.length === 0) {
      return of(null);
    }

    // console.log('Unassigning operatives:', toUnassign);

    // Create an array of unassign observables
    const unassignObservables = toUnassign.map((item) =>
      this.holidaysService.unassignExerciseOperative(item.assignmentId)
    );

    // Execute all unassign operations in parallel
    return forkJoin(unassignObservables);
  }

  private assignNewOperatives(exerciseId: number): Observable<any> {
    const currentlySelected = new Set(this.getSelectedOperatives());
    const newlyAssigned: number[] = [];

    // Find groups that are newly checked (not in original assignments)
    this.operativeTableData.forEach((row) => {
      if (
        currentlySelected.has(row.id) &&
        !this.originalAssignments.has(row.id)
      ) {
        newlyAssigned.push(row.id);
      }
    });

    // If nothing to assign, return empty observable
    if (newlyAssigned.length === 0) {
      return of(null);
    }

    // Format body based on number of groups
    let body: any;
    if (newlyAssigned.length === 1) {
      body = {
        operativa_group: newlyAssigned[0],
        exercise: exerciseId,
        status: 'active',
      };
    } else {
      body = {
        operativa_groups: newlyAssigned,
        exercise: exerciseId,
      };
    }

    // console.log('Assigning new operativa groups:', body);
    return this.holidaysService.postExerciseOperatives(body, exerciseId);
  }

  private updateOperativeAssignments(exerciseId: number): Observable<any> {
    // First unassign, then assign new ones
    return new Observable((observer) => {
      this.unassignOperatives().subscribe({
        next: () => {
          // After unassigning, assign new operatives
          this.assignNewOperatives(exerciseId).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
            },
          });
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }

  private validateDateRange(): void {
    const startDate = this.exerciseForm.get('start_date')?.value;
    const endDate = this.exerciseForm.get('end_date')?.value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      this.dateRangeError = end <= start;
    } else {
      this.dateRangeError = false;
    }

    // Validate initial request dates (start_request_holidays / end_request_holidays)
    const startRequestDate = this.exerciseForm.get(
      'start_request_holidays'
    )?.value;
    const endRequestDate = this.exerciseForm.get('end_request_holidays')?.value;

    if (
      (startRequestDate && !endRequestDate) ||
      (!startRequestDate && endRequestDate)
    ) {
      this.requestDateIncompleteError = true;
      this.requestDateRangeError = false;
    } else if (startRequestDate && endRequestDate) {
      this.requestDateIncompleteError = false;
      const start = new Date(startRequestDate);
      const end = new Date(endRequestDate);
      this.requestDateRangeError = end <= start;
    } else {
      this.requestDateIncompleteError = false;
      this.requestDateRangeError = false;
    }

    // Validate vacation period dates (start_vacation_period / end_vacation_period)
    const startVacationPeriod = this.exerciseForm.get(
      'start_vacation_period'
    )?.value;
    const endVacationPeriod = this.exerciseForm.get(
      'end_vacation_period'
    )?.value;

    if (
      (startVacationPeriod && !endVacationPeriod) ||
      (!startVacationPeriod && endVacationPeriod)
    ) {
      this.vacationDateIncompleteError = true;
      this.vacationDateRangeError = false;
    } else if (startVacationPeriod && endVacationPeriod) {
      this.vacationDateIncompleteError = false;
      const start = new Date(startVacationPeriod);
      const end = new Date(endVacationPeriod);
      this.vacationDateRangeError = end <= start;
    } else {
      this.vacationDateIncompleteError = false;
      this.vacationDateRangeError = false;
    }
  }

  private formatDateForAPI(date: any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    // Validate date range
    this.validateDateRange();
    if (this.dateRangeError) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'La fecha de fin debe ser posterior a la fecha de inicio',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (this.requestDateIncompleteError) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Si completas una fecha de petición inicial, debes completar ambas fechas',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (this.requestDateRangeError) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'La fecha de fin de petición inicial debe ser posterior a la fecha de inicio',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (this.vacationDateIncompleteError) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'Si completas una fecha de vacaciones, debes completar ambas fechas',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (this.vacationDateRangeError) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'La fecha de fin de vacaciones debe ser posterior a la fecha de inicio',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    if (!this.exerciseForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.exerciseForm.controls).forEach((key) => {
        const control = this.exerciseForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updateExercise();
    } else {
      this.createExercise();
    }
  }

  private createExercise(): void {
    const formValue = this.exerciseForm.value;

    const body: any = {
      name: formValue.name,
      year: formValue.year,
      start_date: this.formatDateForAPI(formValue.start_date),
      end_date: this.formatDateForAPI(formValue.end_date),
      active: formValue.active,
    };

    // Add configuration if any fields are provided
    if (
      formValue.start_request_holidays ||
      formValue.end_request_holidays ||
      formValue.start_vacation_period ||
      formValue.end_vacation_period ||
      formValue.continuous_phase ||
      formValue.initial_petition
    ) {
      body.configuration = {
        start_request_holidays: this.formatDateForAPI(
          formValue.start_request_holidays
        ),
        end_request_holidays: this.formatDateForAPI(
          formValue.end_request_holidays
        ),
        start_vacation_period: this.formatDateForAPI(
          formValue.start_vacation_period
        ),
        end_vacation_period: this.formatDateForAPI(
          formValue.end_vacation_period
        ),
        continuous_phase: formValue.continuous_phase,
        initial_petition: formValue.initial_petition,
      };
    }

    this.holidaysService
      .postExercise(body)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          // After exercise is created, save operative assignments
          const exerciseId = response.id;
          this.saveOperativeAssignments(exerciseId).subscribe({
            next: () => {
              this.loaderService.hideLoader();
              this.snackBar.open('Ejercicio creado exitosamente', 'Cerrar', {
                duration: 3000,
              });
              this.router.navigate(['/home/vacations/drivers']);
            },
            error: (error) => {
              this.loaderService.hideLoader();
              console.error('Error saving operative assignments:', error);
              this.snackBar.open('Error al asignar operativas', 'Cerrar', {
                duration: 3000,
              });
            },
          });
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error creating exercise:', error);
          this.snackBar.open('Error al crear el ejercicio', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  private updateExercise(): void {
    if (!this.exerciseId) return;

    const formValue = this.exerciseForm.value;

    const body: any = {
      name: formValue.name,
      year: formValue.year,
      start_date: this.formatDateForAPI(formValue.start_date),
      end_date: this.formatDateForAPI(formValue.end_date),
      active: formValue.active,
    };

    // Add configuration if any fields are provided
    if (
      formValue.start_request_holidays ||
      formValue.end_request_holidays ||
      formValue.start_vacation_period ||
      formValue.end_vacation_period ||
      formValue.continuous_phase ||
      formValue.initial_petition
    ) {
      body.configuration = {
        start_request_holidays: this.formatDateForAPI(
          formValue.start_request_holidays
        ),
        end_request_holidays: this.formatDateForAPI(
          formValue.end_request_holidays
        ),
        start_vacation_period: this.formatDateForAPI(
          formValue.start_vacation_period
        ),
        end_vacation_period: this.formatDateForAPI(
          formValue.end_vacation_period
        ),
        continuous_phase: formValue.continuous_phase,
        initial_petition: formValue.initial_petition,
      };
    }

    this.holidaysService
      .updateExercise(this.exerciseId, body)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          // After exercise is updated, handle operative assignments (unassign + assign)
          this.updateOperativeAssignments(this.exerciseId!).subscribe({
            next: () => {
              this.loaderService.hideLoader();
              this.snackBar.open(
                'Ejercicio actualizado correctamente',
                'Cerrar',
                {
                  duration: 3000,
                }
              );
              this.router.navigate([
                '/home/vacations/drivers/exercise/' +
                  this.exerciseId +
                  '/' +
                  this.year,
              ]);
            },
            error: (error) => {
              this.loaderService.hideLoader();
              console.error('Error updating operative assignments:', error);
              this.snackBar.open(
                'Error al actualizar asignaciones de operativas',
                'Cerrar',
                {
                  duration: 3000,
                }
              );
            },
          });
        },
        error: (error) => {
          console.error('Error updating exercise:', error);
          this.loaderService.hideLoader();
          this.snackBar.open('Error al actualizar el ejercicio', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate([
        '/home/vacations/drivers/exercise/' + this.exerciseId,
        '/' + this.year,
      ]);
    } else {
      this.router.navigate(['/home/vacations/drivers']);
    }
  }
}
